import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { ChasePaymentUkRequest } from './cmg-chase-payment-uk-request';
import { OrderWithContext, SubmitOrderRequestPaymentMethodType, TokenizedPaymentCard } from '@/models';

@Component({
    name: 'CmgChaseUkForm',
})

export default class CmgChaseUkForm extends Vue {
    // #region Public Properties
    public isPaymentProviderInitializing = true;
    public errorMessages: string[] = [];

    private checkoutId: string = '';

    @Prop({ required: true, type: Boolean })
    public oneTimePayment!: boolean;

    @Prop({ required: true, type: String })
    public providerId!: string;

    get order(): OrderWithContext | null {
        return this.$services.order.getCurrentOrder();
    }

    get showErrors() {
        return this.errorMessages.length != 0;
    }

    public async mounted(): Promise<void> {
        window.removeEventListener('message', this.listenerFunction);
        window.addEventListener("message", this.listenerFunction);

        try {
            await this.loadIframe();
        } catch (e: any) {
            this.errorMessages.push('Oops an error was encontered while fetching the checkout id from the service. Please try again!');
        }
    }

    public beforeDestroy() {
        this.checkoutId = '';
    }

    private listenerFunction(ev: Event) {
        const event = ev as MessageEvent;
        if (event && event.origin === new URL(this.$services.payment.getChaseUKCallbackUrl()).origin  &&
            event.data && JSON.parse(event.data).id === this.checkoutId) {
            this.completePayment();
        }
    }

    private async loadIframe() {
        this.checkoutId = await this.getCheckoutId();
        const iframe = this.$refs.frame as HTMLIFrameElement;
        if (iframe) {
            iframe.src = `${this.$services.config.getChaseUkBaseUrl()}?checkoutId=${this.checkoutId}&authorizationType=${this.oneTimePayment ? 'onetimepayment' : 'addtowallet'}`;
        }
    }

    private async getCheckoutId(): Promise<string> {
        if (!this.providerId) {
            throw new Error('A valid provider is required');
        }

        try {
            const request: ChasePaymentUkRequest = new ChasePaymentUkRequest();

            request.preauth = this.oneTimePayment;
            if (this.oneTimePayment) {
                request.amount = this.order!.order.orderTotalAmount;
                request.restaurantId = this.order?.order.restaurantId;
            }
            const response = await this.$services.transaction.initializeRequest(this.providerId, request);
            if ((!response.uId || !response.sessionId) && !response.checkoutId) {
                throw Error('No request data returned by the service');
            } else {
                return response.checkoutId || '';
            }
        } catch (error: any) {
            this.$services.logger.error('An error was encountered while retrieving a checkout id from server.', error);
            throw new Error('The request session could not be fulfilled');
        } finally {
            this.isPaymentProviderInitializing = false;
        }
    }

    public async completePayment(): Promise<void> {
        this.isPaymentProviderInitializing = true;
        this.errorMessages = [];
        if (this.providerId && this.checkoutId && this.checkoutId !== '') {
            try {
                const tokenizedCardResponse = !this.oneTimePayment
                    ? await this.$services.transaction.getTransactionToken(this.providerId, this.checkoutId)
                    : await this.$services.transaction.getTransactionToken(this.providerId, this.checkoutId, undefined, this.order?.order.restaurantId, this.order?.order.orderId);
                const tokenizedCard = {
                    cardHolderName: tokenizedCardResponse.cardHolderName,
                    expiration: tokenizedCardResponse.expiration,
                    paymentMethod: tokenizedCardResponse.paymentMethod,
                    tokenizedAccountNumber: tokenizedCardResponse.paymentToken,
                    paymentProviderId: this.providerId,
                    lastFourAccountNumbers: tokenizedCardResponse.last4,
                    transactionId: tokenizedCardResponse.authTransactionId?.toString()
                } as TokenizedPaymentCard;

                // if we landed here from one time payment modal, we want to just set the payment method
                // else, just saving the card
                if (this.oneTimePayment) {
                    this.setOneTimePaymentMethod(tokenizedCard);
                } else {
                    await this.savePaymentCard(tokenizedCard);
                }

            } catch (err: any) {
                await this.loadIframe();
                this.errorMessages.push('Please verify information and try again!');
                this.isPaymentProviderInitializing = false;
            }
        }
        this.isPaymentProviderInitializing = false;
    }

    private async savePaymentCard(tokenizedCard: any): Promise<void> {
        await this.$services.transaction
            .savePaymentCard(tokenizedCard)
            .then(() => {
                this.$services.transaction.refreshPaymentCards();
                this.closeModal();
            })
            .catch(() => {
                this.clearErrorMessages();
                this.errorMessages.push('Could not save credit card. Please try again later.');
            });
    }

    private setOneTimePaymentMethod(tokenizedCard: TokenizedPaymentCard) {
        this.$services.checkout.setPaymentMethods([
            {
                cardHolderName: tokenizedCard.cardHolderName!,
                creditCardSingleUseToken: tokenizedCard.tokenizedAccountNumber!,
                creditCardType: tokenizedCard.paymentMethod!.replace(/-/g, ' '),
                creditCardExpiration: tokenizedCard.expiration,
                paymentType: SubmitOrderRequestPaymentMethodType.CreditCard,
                paymentProviderId: this.providerId,
                lastFourAccountNumbers: tokenizedCard.lastFourAccountNumbers,
                transactionId: tokenizedCard.transactionId,
                preauthorizedAmount: this.order!.order.orderTotalAmount
            },
        ]);
        if (tokenizedCard.expiration) {
            tokenizedCard.expirationMonth = tokenizedCard.expiration.substr(0, 2);
            tokenizedCard.expirationYear = tokenizedCard.expiration.substr(2, 2);
        }
        tokenizedCard.paymentMethod = tokenizedCard.paymentMethod.toLowerCase();
        tokenizedCard.isOneTimePaymentCard = true;
        this.$services.checkout.setOneTimePayCard(tokenizedCard);
        this.closeModal();
    }

    private closeModal(): void {
        window.removeEventListener('message', this.listenerFunction);
        this.checkoutId = '';
        const args = this.$appStore.state.ui.modals.addCreditOrDebitCardModal.args;
        if (args && args.onClose) {
            args.onClose();
        }
        this.$services.ui.setModalVisibility('addCreditOrDebitCardModal', false);
    }

    private clearErrorMessages(): void {
        this.errorMessages.length = 0;
    }

}